"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Mail, ShieldCheck, Clock } from "lucide-react";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="bg-[#14B8A6]/20 p-4 rounded-lg mb-4">
          <Mail className="size-8 text-[#14B8A6]" />
        </div>
        <h3 className="text-2xl font-default font-semibold mb-2 text-foreground">
          ¡Mensaje enviado!
        </h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-border hover:bg-accent">
          Enviar otro mensaje
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="nombre" rules={{
        required: "El nombre es obligatorio",
        minLength: {
          value: 2,
          message: "Mínimo 2 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Nombre completo</FormLabel>
              <FormControl>
                <Input placeholder="Ej. Juan Pérez" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo electrónico es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Correo electrónico no válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Correo electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="juan@ejemplo.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="empresa" render={({
        field
      }) => <FormItem>
              <FormLabel>Empresa (opcional)</FormLabel>
              <FormControl>
                <Input placeholder="Nombre de tu empresa" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="giro" render={({
        field
      }) => <FormItem>
              <FormLabel>Giro (opcional)</FormLabel>
              <FormControl>
                <Input placeholder="Ej. Notaría, joyería, inmobiliaria" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="asunto" rules={{
      required: "El asunto es obligatorio"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Asunto</FormLabel>
            <FormControl>
              <Input placeholder="¿En qué podemos ayudarte?" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="mensaje" rules={{
      required: "El mensaje es obligatorio",
      minLength: {
        value: 10,
        message: "Mínimo 10 caracteres"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Mensaje</FormLabel>
            <FormControl>
              <Textarea placeholder="Escribe tu mensaje aquí..." className="min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[200px] bg-[#14B8A6] hover:bg-[#14B8A6]/90 text-white border-none shadow-md" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Enviando...
          </> : "Enviar"}
      </Button>
    </Form>;
}
export default function FormularioDeContacto() {
  return <section data-section-id="REEMPLAZAR" id="formulario-de-contacto" className="bg-background py-16 md:py-24 lg:py-32 relative overflow-hidden text-foreground">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

          <div className="lg:col-span-5 space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-default font-bold tracking-tight text-foreground mb-4">
                Ponte en contacto con nosotros
              </h2>
              <p className="text-lg text-muted-foreground">
                Estamos aquí para ayudarte a cumplir con tus obligaciones en materia de prevención de lavado de dinero. Completa el formulario y un asesor se comunicará contigo a la brevedad.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                  <Mail className="size-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-default font-semibold text-foreground text-lg">Correo electrónico</h4>
                  <p className="text-muted-foreground mt-1 font-mono">contacto@notalia.app</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                  <Clock className="size-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-default font-semibold text-foreground text-lg">Tiempo de respuesta</h4>
                  <p className="text-muted-foreground mt-1">Respondemos en menos de 24 horas hábiles.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                  <ShieldCheck className="size-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-default font-semibold text-foreground text-lg">Tus datos protegidos</h4>
                  <p className="text-muted-foreground mt-1">Tratamos tu información conforme a nuestro aviso de privacidad.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="dark bg-background text-foreground border border-border rounded-2xl p-6 md:p-10 shadow-2xl">
              <div className="mb-8">
                <h3 className="text-2xl font-default font-semibold text-foreground">
                  Envíanos un mensaje
                </h3>
                <p className="text-muted-foreground mt-2">
                  Tus datos están seguros con nosotros.
                </p>
              </div>

              <FormProvider formId="contact-form-main" sectionName="formulario-de-contacto" defaultValues={{
              nombre: "",
              email: "",
              empresa: "",
              giro: "",
              asunto: "",
              mensaje: ""
            }} validationRules={{
              nombre: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              asunto: {
                required: true
              },
              mensaje: {
                required: true,
                minLength: 10
              }
            }} submitText="Enviar" successMessage="¡Gracias por contactarnos! Hemos recibido tu mensaje y te responderemos pronto.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}